/**
 * Right now, this should only be shown to users who are on the free plan.
 */
import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as planFeatures from "shared/planFeatures";
import * as subscriptionPlans from "shared/subscriptionPlans";
import stores from "stores/stores";
import * as upsellTimer from "upsellTimer";

const isBookmarklet = window.location.pathname === "/bookmarklet";

/**
 * Whether the panel treats the user as an existing Premium subscriber, offering
 * a plan change via /account rather than a new subscription via /checkout.
 */
export const hasPremium = (user: { subscriptionPlan?: string }) =>
  user.subscriptionPlan === "standard" ||
  user.subscriptionPlan === "freeForever";

interface AccountPanelProps {
  accountPage?: boolean;
  showTitle?: boolean;
  phraseLimit?: boolean;
  plus?: boolean;
  smartDefinitions?: boolean;
  /** Receives the "See premium plans" link, which only free-path users get */
  checkoutLinkRef?: preact.Ref<HTMLAnchorElement>;
}

interface AccountPanelState {
  localMode?: boolean;
  claimingExtraPhrases: boolean;
}

export class AccountPanel extends preact.Component<
  AccountPanelProps,
  AccountPanelState
> {
  state: AccountPanelState = {
    claimingExtraPhrases: false,
  };

  componentDidMount() {
    upsellTimer.reset();
    localAPI.isLocalMode().then((localMode) => {
      this.setState({ localMode });
    });
  }

  goToCheckout = (event: MouseEvent) => {
    if (isBookmarklet) {
      event.preventDefault();
      event.stopPropagation();

      const protocol =
        window.location.hostname === "localhost" ? "http://" : "https://";
      window.top.location.href = protocol + window.location.host + "/checkout";
    }
  };

  loginLink = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    $("#loginButton").click();
  };

  signupLink = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    $("#signUpButton").click();
  };

  extraPhrases = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ claimingExtraPhrases: true });
    readlangClient.request("POST", "/api/user/claimExtraPhrases", {}, () => {
      const acheivements = [...(stores.user.state.value.acheivements || [])];
      if (acheivements.indexOf("extraPhrases") === -1) {
        acheivements.push("extraPhrases");
      }
      stores.user.patch({ acheivements });
      modalController.cancel();
    });
  };

  continue = () => {
    modalController.cancel();
  };

  render() {
    if (this.state.localMode === undefined) {
      return null;
    }

    const user = stores.user.state.value;
    const localMode = this.state.localMode;

    let extraPhrases = false;
    let phraseLimit: undefined | number = undefined;

    if (this.props.phraseLimit) {
      const acheivements = user.acheivements || [];
      if (acheivements.indexOf("extraPhrases") === -1) {
        // DISABLE extra phrases
        extraPhrases = true;
      }
      phraseLimit =
        user.phraseLimit || subscriptionPlans.permissions.free.phrases;
    }

    // const allPhrasesLimit = this.options.allPhrasesLimit ? true : false;

    /** Show Premium Plus instead of Premium */
    const plus = this.props.plus;

    /** Check if current learning language supports audio */
    const currentLang = stores.user.currentLearningLanguage.value;
    const supportsAudio = languageMap.map[currentLang]?.whisper === true;

    if (localMode) {
      return (
        <div>
          <h2>Premium {plus ? "Plus" : ""} Plan</h2>
          <ul>
            <li>${plus ? 15 : 6} / month</li>
            <li>
              ${plus ? 120 : 48} / year{" "}
              <small>
                <i>(only ${plus ? 10 : 4} / month)</i>
              </small>
            </li>
          </ul>
          <a href="#" class="signupLink" onClick={this.signupLink}>
            Sign in
          </a>{" "}
          to try the full free version first.
        </div>
      );
    }

    const freePlan = (
      <div class="plan2">
        <h4>
          Free Limited <span class="currentPlan">(Current plan)</span>
        </h4>
        <div class="price">$0</div>
        <table>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              Unlimited <span class="highlight">single word</span> translations
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>10</strong> phrase translations per day
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <span class="subtle">
                (<strong>6</strong> words per phrase)
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>10</strong> context explanations per day
            </td>
          </tr>
          <tr class="notIncluded">
            <td>
              <div class="cross"></div>
            </td>
            <td>Written conversation practice</td>
          </tr>
          {supportsAudio && (
            <tr class="notIncluded">
              <td>
                <div class="cross"></div>
              </td>
              <td>Audio upload and transcription</td>
            </tr>
          )}
          <tr class="notIncluded">
            <td>
              <div class="cross"></div>
            </td>
            <td>Generate stories with the Story Bot</td>
          </tr>
          <tr class="notIncluded">
            <td>
              <div class="cross"></div>
            </td>
            <td>Context-aware translations</td>
          </tr>
          <tr class="notIncluded noMobile">
            <td>
              <div class="cross"></div>
            </td>
            <td>Practice or export flashcards from specific books</td>
          </tr>
          <tr class="notIncluded noMobile">
            <td>
              <div class="cross"></div>
            </td>
            <td>0 streak repairs</td>
          </tr>
        </table>

        {phraseLimit && extraPhrases ? (
          <>
            <h5>1 time only freebie:</h5>
            {this.state.claimingExtraPhrases ? (
              <img src="/content/throbber.gif" />
            ) : (
              <a
                class="secondaryLink extraPhrases"
                href="#"
                onClick={this.extraPhrases}
              >
                Get {phraseLimit} more phrase translations
              </a>
            )}
          </>
        ) : null}
      </div>
    );
    const premiumPlan = (
      mode: "current" | "new" | "upgrade",
      plusPlan: boolean
    ) => (
      <div class="plan2 suggested">
        <h4>
          Premium {plusPlan ? "Plus" : ""}{" "}
          {mode === "current"
            ? "(Current Plan)"
            : plusPlan
              ? ""
              : "plans starting from..."}
        </h4>
        <span class="currentPlanPlaceholder">&nbsp;</span>
        <div class="price">
          ${plusPlan ? 10 : 4}
          <small> /mo</small>
        </div>
        <table>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Unlimited</strong> single word translations
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Unlimited</strong> phrase translations
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <span class="subtle">
                (<strong>{planFeatures.premiumPhraseWords}</strong> words per
                phrase)
              </span>
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Unlimited</strong> context explanations
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Written conversation</strong> practice
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <span class="subtle">
                {plusPlan ? (
                  <>
                    (<strong>Unlimited</strong>)
                  </>
                ) : (
                  <>
                    (<strong>{planFeatures.premiumChatsPerDay}</strong> per day)
                  </>
                )}
              </span>
            </td>
          </tr>
          {supportsAudio && (
            <tr>
              <td>
                <div class="checkmark"></div>
              </td>
              <td>
                <strong>Audio upload</strong> and transcription
              </td>
            </tr>
          )}
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Generate stories</strong> with the Story Bot
            </td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>Context-aware</strong> translations
            </td>
          </tr>
          <tr class="noMobile">
            <td>
              <div class="checkmark"></div>
            </td>
            <td>Practice or export flashcards from specific books</td>
          </tr>
          <tr>
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>High quality voices</strong>{" "}
              {plusPlan
                ? "for speaking whole texts"
                : "for speaking words and phrases"}
            </td>
          </tr>
          <tr class="noMobile">
            <td>
              <div class="checkmark"></div>
            </td>
            <td>
              <strong>2</strong> streak repairs per month
            </td>
          </tr>
          {plusPlan ? (
            <tr>
              <td>
                <div class="checkmark"></div>
              </td>
              <td>
                <strong>Better AI</strong> for AI powered features
              </td>
            </tr>
          ) : null}
        </table>

        {mode === "current" ? null : (
          <>
            <div class="cancelAnytime">Cancel anytime</div>
            {mode === "new" ? (
              <a
                class="goToCheckout"
                href="/checkout"
                onClick={this.goToCheckout}
                ref={this.props.checkoutLinkRef}
              >
                See premium plans
              </a>
            ) : (
              <a
                class="goToCheckout"
                href="/account"
                onClick={this.goToCheckout}
              >
                Upgrade
              </a>
            )}
          </>
        )}
      </div>
    );

    const userHasPremium = hasPremium(user);

    const currentPlan = userHasPremium
      ? premiumPlan("current", false)
      : freePlan;

    const suggestedPlan = premiumPlan(userHasPremium ? "upgrade" : "new", plus);

    return (
      <div class="accountPanel2">
        {(this.props.showTitle ?? true) && (
          <h2 class="spacing" style={{ textAlign: "center" }}>
            Go Premium
          </h2>
        )}

        <div class="planContainer2">
          {deviceDetect.isSmall() ? (
            <>
              {suggestedPlan}
              {currentPlan}
            </>
          ) : (
            <>
              {currentPlan}
              {suggestedPlan}
            </>
          )}
        </div>
      </div>
    );
  }
}
